import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Edit, Trash2, Crown, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMembershipsPage = () => {
  const { token } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    price_monthly: 500,
    price_yearly: 5000,
    features: [],
    priority_booking: false,
    free_open_play: false,
    discount_percentage: 10
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchMemberships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMemberships = async () => {
    try {
      const res = await fetch(`${API}/admin/memberships`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setMemberships(await res.json());
    } catch (error) {
      console.error('Failed to fetch memberships:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveMembership = async () => {
    try {
      const res = await fetch(`${API}/admin/memberships`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success('Membership created');
        setShowModal(false);
        resetForm();
        fetchMemberships();
      }
    } catch (error) {
      toast.error('Failed to create membership');
    }
  };

  const deleteMembership = async (membershipId) => {
    if (!confirm('Delete this membership tier?')) return;
    try {
      await fetch(`${API}/admin/memberships/${membershipId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Membership deleted');
      fetchMemberships();
    } catch (error) {
      toast.error('Failed to delete membership');
    }
  };

  const resetForm = () => {
    setForm({
      name: '', description: '', price_monthly: 500, price_yearly: 5000,
      features: [], priority_booking: false, free_open_play: false, discount_percentage: 10
    });
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setForm(p => ({ ...p, features: [...p.features, newFeature.trim()] }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setForm(p => ({ ...p, features: p.features.filter((_, i) => i !== index) }));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Memberships</h1>
            <p className={`${theme.textMuted} mt-1`}>Manage membership tiers</p>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" /> Add Tier
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {memberships.map((membership) => (
              <Card key={membership.membership_id} className={theme.card}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Crown className={`h-6 w-6 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                    <h3 className={`text-xl font-bold ${theme.text}`}>{membership.name}</h3>
                  </div>
                  <p className={`text-sm mb-4 ${theme.textMuted}`}>{membership.description}</p>
                  
                  <div className="mb-4">
                    <p className={`text-3xl font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      ₱{membership.price_monthly}
                      <span className={`text-sm ${theme.textSubtle}`}>/month</span>
                    </p>
                    <p className={`text-sm ${theme.textSubtle}`}>or ₱{membership.price_yearly}/year</p>
                  </div>

                  <div className="space-y-2 mb-4">
                    {membership.features?.map((feature, idx) => (
                      <div key={idx} className={`flex items-center gap-2 text-sm ${theme.textMuted}`}>
                        <Check className={`h-4 w-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                        {feature}
                      </div>
                    ))}
                    <div className={`flex items-center gap-2 text-sm ${theme.textMuted}`}>
                      <Check className={`h-4 w-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                      {membership.discount_percentage}% booking discount
                    </div>
                    {membership.priority_booking && (
                      <div className={`flex items-center gap-2 text-sm ${theme.textMuted}`}>
                        <Check className={`h-4 w-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                        Priority booking
                      </div>
                    )}
                    {membership.free_open_play && (
                      <div className={`flex items-center gap-2 text-sm ${theme.textMuted}`}>
                        <Check className={`h-4 w-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                        Free open play
                      </div>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className={`w-full text-rose-400 hover:text-rose-300 ${theme.btnOutline}`}
                    onClick={() => deleteMembership(membership.membership_id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className={`w-full max-w-lg my-8 ${theme.modalBg}`}>
              <CardHeader><CardTitle className={theme.text}>Add Membership Tier</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={theme.text}>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                    className={theme.input} placeholder="Pro Member" />
                </div>
                <div>
                  <Label className={theme.text}>Description</Label>
                  <Input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                    className={theme.input} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={theme.text}>Monthly Price (₱)</Label>
                    <Input type="number" value={form.price_monthly}
                      onChange={(e) => setForm(p => ({ ...p, price_monthly: parseFloat(e.target.value) }))}
                      className={theme.input} />
                  </div>
                  <div>
                    <Label className={theme.text}>Yearly Price (₱)</Label>
                    <Input type="number" value={form.price_yearly}
                      onChange={(e) => setForm(p => ({ ...p, price_yearly: parseFloat(e.target.value) }))}
                      className={theme.input} />
                  </div>
                </div>
                <div>
                  <Label className={theme.text}>Discount %</Label>
                  <Input type="number" value={form.discount_percentage}
                    onChange={(e) => setForm(p => ({ ...p, discount_percentage: parseFloat(e.target.value) }))}
                    className={theme.input} />
                </div>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 ${theme.text}`}>
                    <input type="checkbox" checked={form.priority_booking}
                      onChange={(e) => setForm(p => ({ ...p, priority_booking: e.target.checked }))} />
                    Priority Booking
                  </label>
                  <label className={`flex items-center gap-2 ${theme.text}`}>
                    <input type="checkbox" checked={form.free_open_play}
                      onChange={(e) => setForm(p => ({ ...p, free_open_play: e.target.checked }))} />
                    Free Open Play
                  </label>
                </div>
                <div>
                  <Label className={theme.text}>Features</Label>
                  <div className="flex gap-2">
                    <Input value={newFeature} onChange={(e) => setNewFeature(e.target.value)}
                      className={theme.input} placeholder="Add feature..." />
                    <Button type="button" onClick={addFeature} className="bg-emerald-600 hover:bg-emerald-700">Add</Button>
                  </div>
                  <div className="mt-2 space-y-1">
                    {form.features.map((f, i) => (
                      <div key={i} className={`flex items-center justify-between text-sm px-2 py-1 rounded ${isDarkMode ? 'text-slate-300 bg-slate-700/30' : 'text-gray-700 bg-gray-100'}`}>
                        {f}
                        <button onClick={() => removeFeature(i)} className="text-rose-400 hover:text-rose-300">×</button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className={`flex-1 ${theme.btnOutline}`} onClick={() => setShowModal(false)}>Cancel</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={saveMembership}>Create</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMembershipsPage;
